import React, { useEffect, useRef } from "react";
import { Check } from "lucide-react";
import { theme } from "../../assets/theme/theme";

const TripStepperTimeline = ({ steps = [], isTripComplete = false }) => {
  const stepItemRefs = useRef([]);

  const checkIsCompleted = (s) => {
    if (isTripComplete) return true;
    const st = s?.status ? String(s.status).toUpperCase() : "";
    return (
      st === "COMPLETED" ||
      st === "SKIPPED" ||
      (s.type === "PICKUP" && (st === "IN_PROGRESS" || st === "IN PROGESS"))
    );
  };

  /* ---------------- GENERATE LABELS (P1, P2, D1...) ---------------- */
  let pickupCount = 0;
  let dropoffCount = 0;
  const labeledSteps = steps.map((s) => {
    if (s.type === "PICKUP") {
      pickupCount += 1;
      return {
        ...s,
        label: `P${pickupCount}`,
        subLabel: `Pickup ${pickupCount}`,
        isCompleted: checkIsCompleted(s),
      };
    }
    dropoffCount += 1;
    return {
      ...s,
      label: `D${dropoffCount}`,
      subLabel: `Drop ${dropoffCount}`,
      isCompleted: checkIsCompleted(s),
    };
  });

  const activeStepIndex = isTripComplete
    ? -1
    : labeledSteps.findIndex((s) => !s.isCompleted);

  /* ---------------- AUTO-SCROLL ACTIVE NODE ---------------- */
  useEffect(() => {
    if (activeStepIndex >= 0 && stepItemRefs.current[activeStepIndex]) {
      stepItemRefs.current[activeStepIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeStepIndex, isTripComplete]);

  if (!labeledSteps.length) return null;

  return (
    <div className="w-full px-4 pt-1.5 pb-2">
      <div
        className="overflow-x-auto no-scrollbar scroll-smooth py-1"
        style={{ scrollSnapType: "x mandatory" }}
      >
        <div className="flex items-center justify-between w-full max-w-sm mx-auto">
          {labeledSteps.map((s, idx) => {
            const isCompleted = s.isCompleted;
            const isActive = !isTripComplete && idx === activeStepIndex;

            return (
              <React.Fragment key={s.step || s.stop_id || idx}>
                {/* Node Pill */}
                <div
                  ref={(el) => (stepItemRefs.current[idx] = el)}
                  className="flex flex-col items-center shrink-0"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-all shadow-xs z-10"
                    style={{
                      fontFamily: "DMMono-Medium",
                      backgroundColor: isCompleted
                        ? "#10B981" // Completed: Emerald
                        : isActive
                        ? theme.colors.brand // Active: Red
                        : "#F1F5F9", // Inactive: Slate
                      color: isCompleted || isActive ? "#FFFFFF" : "#64748B",
                      border: `2px solid ${
                        isCompleted
                          ? "#10B981"
                          : isActive
                          ? theme.colors.brand
                          : "#CBD5E1"
                      }`,
                    }}
                  >
                    {isCompleted ? (
                      <Check className="w-4 h-4 stroke-[3]" />
                    ) : (
                      s.label
                    )}
                  </div>

                  <span
                    className="text-[9px] font-bold uppercase tracking-wider mt-1 select-none"
                    style={{
                      fontFamily: "DMSans-Medium",
                      color: isCompleted
                        ? "#10B981"
                        : isActive
                        ? theme.colors.bitumen
                        : "#94A3B8",
                    }}
                  >
                    {s.subLabel}
                  </span>
                </div>

                {/* Connecting Rail */}
                {idx < labeledSteps.length - 1 && (
                  <div
                    className="flex-1 h-0.5 mx-1.5 self-center mb-4 transition-colors"
                    style={{
                      backgroundColor: isCompleted ? "#10B981" : "#E2E8F0",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TripStepperTimeline;